import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell, ReferenceLine,
  RadialBarChart, RadialBar,
} from "recharts";
import { Badge } from "@/components/ui/badge";
import {
  Sparkles, Award, TrendingUp, Fingerprint, Brain, Compass, Settings2, Wallet, Users, Rocket,
  ShieldCheck, GraduationCap, Printer, Lock, AlertTriangle, BookOpen, Timer, Lightbulb, HeartPulse, Palette, ScrollText,
  Loader2, Info,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { api } from "@/lib/api";
import { tr, trDim } from "@/lib/reportI18n";
import { BrandLogo } from "@/components/BrandLogo";
import { ArchetypeGrid } from "@/components/report/ArchetypeGrid";
import { DonutChart } from "@/components/report/DonutChart";
import { RIASEC_INFO, PERSONALITY_INFO, VALUES_INFO, humanize } from "@/components/report/dimensionInfo";

const GROUP_COLORS = {
  career_interests: "#4F46E5",
  core_personality: "#06B6D4",
  cognitive_aptitude: "#8B5CF6",
  career_values: "#F59E0B",
  professional_strengths: "#10B981",
  leadership_interpersonal: "#EC4899",
  entrepreneurship: "#3B82F6",
};

const DIM_GROUPS = [
  { key: "career_interests", label: "Career Interests (RIASEC)", icon: Compass, dims: ["realistic", "investigative", "artistic", "social", "enterprising", "conventional"], narrativeKey: "interest_profile", chart: "radar" },
  { key: "core_personality", label: "Core Personality", icon: Brain, dims: ["openness", "conscientiousness", "extraversion", "agreeableness", "emotional_stability", "honesty_humility"], narrativeKey: "personality_profile", chart: "bar", archetypeInfo: PERSONALITY_INFO },
  { key: "cognitive_aptitude", label: "Cognitive & Professional Aptitude", icon: Settings2, dims: ["numerical_reasoning", "verbal_reasoning", "abstract_logical_reasoning", "spatial_reasoning", "mechanical_systems_reasoning", "accuracy_data_attention"], narrativeKey: "cognitive_aptitude_profile", chart: "bar" },
  { key: "career_values", label: "Career Values & Motivators", icon: Wallet, dims: ["achievement_mastery", "autonomy_independence", "security_stability", "service_social_impact", "recognition_status_reward", "lifestyle_relationships_belonging"], narrativeKey: "career_values_profile", chart: "bar", archetypeInfo: VALUES_INFO },
  { key: "professional_strengths", label: "Professional Strengths & Work Style", icon: Award, dims: ["curiosity_learning", "strategic_thinking", "creativity_ideation", "execution_perseverance", "organization_planning", "detail_quality_orientation", "adaptability_flexibility", "resilience_stress_tolerance"], narrativeKey: "work_style_profile", chart: "bar" },
  { key: "leadership_interpersonal", label: "Interpersonal & Leadership Potential", icon: Users, dims: ["self_regulation", "leadership_influence", "empathy_social_intelligence", "teamwork_collaboration", "conflict_management_negotiation", "decision_making_ethical_judgment"], narrativeKey: "leadership_interpersonal", chart: "bar" },
  { key: "entrepreneurship", label: "Entrepreneurship & Innovation", icon: Rocket, dims: ["initiative_ambiguity_tolerance", "entrepreneurial_orientation"], narrativeKey: "entrepreneurship_innovation", chart: "bar" },
];

const CAREER_FIT_AXES = [
  { key: "interest_alignment", label: "Interest" },
  { key: "aptitude_alignment", label: "Aptitude" },
  { key: "personality_alignment", label: "Personality" },
  { key: "values_alignment", label: "Values" },
  { key: "work_environment_alignment", label: "Work Env." },
  { key: "professional_behaviour_alignment", label: "Prof. Behaviour" },
];

const SCHOLAR_CITATIONS = [
  "Holland's Hexagonal Model of Occupational Themes (RIASEC)",
  "Costa & McCrae's Five-Factor Personality Architecture (NEO-PI-R)",
  "Schein's Career Anchors & Work Values Framework",
  "Recent meta-analyses by contemporary vocational psychologists (2020-2026)",
];

const DOMAIN_FIT_META = {
  engineering_technology: { label: "Engineering & Technology", icon: Settings2, blurb: "Draws on realistic, investigative and numerical/spatial reasoning strengths." },
  medical_healthcare: { label: "Medical & Healthcare", icon: HeartPulse, blurb: "Draws on investigative curiosity, empathy and resilience under pressure." },
  chartered_accountancy_finance: { label: "Chartered Accountancy & Finance", icon: Wallet, blurb: "Draws on structure, numerical accuracy and conscientious follow-through." },
  creative_arts_design: { label: "Creative Arts & Design", icon: Palette, blurb: "Draws on artistic expression, imagination and openness to new ideas." },
};

const FIT_TIER_COLORS = {
  "Strong Fit": "bg-emerald-100 text-emerald-700 border-emerald-200",
  "Good Fit": "bg-cyan-100 text-cyan-700 border-cyan-200",
  "Moderate Fit": "bg-amber-100 text-amber-700 border-amber-200",
  "Emerging Fit": "bg-slate-100 text-slate-600 border-slate-200",
};

const SJT_TIER_COLORS = {
  "Considered Response": "bg-emerald-100 text-emerald-700 border-emerald-200",
  "Reasonable Response": "bg-cyan-100 text-cyan-700 border-cyan-200",
  "Worth Reflecting On": "bg-amber-100 text-amber-700 border-amber-200",
};

const CONFIDENCE_COLORS = {
  High: "border-emerald-400 bg-emerald-50 text-emerald-700",
  Moderate: "border-amber-400 bg-amber-50 text-amber-700",
  Caution: "border-rose-400 bg-rose-50 text-rose-700",
};
const CONFIDENCE_PCT = { High: 100, Moderate: 66, Caution: 33 };
const CONFIDENCE_HEX = { High: "#10B981", Moderate: "#F59E0B", Caution: "#F43F5E" };

const fadeUp = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-40px" },
  transition: { duration: 0.4 },
};

const Card = ({ children, testId, className = "" }) => (
  <motion.div {...fadeUp} className={cn("rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 print:break-inside-avoid", className)} data-testid={testId}>
    {children}
  </motion.div>
);

const ListSection = ({ icon: Icon, title, items, testId }) =>
  items && items.length > 0 ? (
    <Card testId={testId}>
      <h3 className="font-heading text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
        <Icon className="h-5 w-5 text-indigo-600" /> {title}
      </h3>
      <ul className="space-y-2 list-disc list-inside text-slate-600 text-sm">
        {items.map((d, i) => <li key={i}>{d}</li>)}
      </ul>
    </Card>
  ) : null;

function GroupBarChart({ group, scores, lang }) {
  const data = group.dims.map((d) => ({ name: trDim(d, lang) || humanize(d), value: scores?.[d] ?? 0 }));
  const color = GROUP_COLORS[group.key];
  return (
    <ResponsiveContainer width="100%" height={Math.max(180, data.length * 44)}>
      <BarChart data={data} layout="vertical" margin={{ left: 8, right: 24 }}>
        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#F1F5F9" />
        <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11, fill: "#94A3B8" }} />
        <YAxis type="category" dataKey="name" width={170} tick={{ fontSize: 12, fill: "#475569" }} />
        <Tooltip cursor={{ fill: "#F8FAFC" }} />
        <Bar dataKey="value" radius={[0, 6, 6, 0]} isAnimationActive animationDuration={800}>
          {data.map((_, i) => <Cell key={i} fill={color} />)}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

function InterestRadar({ dims, scores, lang }) {
  const data = dims.map((d) => ({ dim: trDim(d, lang) || humanize(d), value: scores?.[d] ?? 0 }));
  return (
    <ResponsiveContainer width="100%" height={320}>
      <RadarChart data={data} outerRadius="65%" margin={{ top: 24, right: 48, bottom: 24, left: 48 }}>
        <PolarGrid stroke="#E2E8F0" />
        <PolarAngleAxis dataKey="dim" tick={{ fontSize: 11, fill: "#475569" }} />
        <PolarRadiusAxis domain={[0, 100]} tick={{ fontSize: 9, fill: "#CBD5E1" }} />
        <Radar dataKey="value" stroke={GROUP_COLORS.career_interests} fill={GROUP_COLORS.career_interests} fillOpacity={0.35} isAnimationActive animationDuration={800} />
        <Tooltip />
      </RadarChart>
    </ResponsiveContainer>
  );
}

function RiasecShowcase({ dims, scores, lang }) {
  return (
    <div className="grid lg:grid-cols-[1fr_1.1fr] gap-6 items-center">
      <InterestRadar dims={dims} scores={scores} lang={lang} />
      <ArchetypeGrid dims={dims} scores={scores} infoMap={RIASEC_INFO} testId="riasec-archetype-grid" lang={lang} />
    </div>
  );
}

function DimensionShareDonut({ scores, dims, lang }) {
  const present = dims.filter((d) => scores?.[d] != null);
  const top = [...present].sort((a, b) => (scores[b] ?? 0) - (scores[a] ?? 0)).slice(0, 4);
  const total = top.reduce((sum, d) => sum + (scores[d] || 0), 0) || 1;
  const data = top.map((d) => ({ name: tr(RIASEC_INFO[d]?.label, lang) || humanize(d), value: Math.round(((scores[d] || 0) / total) * 100), color: RIASEC_INFO[d]?.color || "#4F46E5" }));
  const topScore = Math.round(scores?.[top[0]] ?? 0);
  return <DonutChart data={data} centerLabel={`${topScore}%`} centerSubLabel={tr("Archetype Fit", lang)} testId="report-donut-chart" />;
}

function ConfidenceGauge({ level }) {
  const pct = CONFIDENCE_PCT[level] ?? 50;
  const color = CONFIDENCE_HEX[level] ?? "#94A3B8";
  const data = [{ name: "confidence", value: pct, fill: color }];
  return (
    <div className="relative h-28 w-28 shrink-0" data-testid="confidence-gauge">
      <ResponsiveContainer width="100%" height="100%">
        <RadialBarChart data={data} innerRadius="70%" outerRadius="100%" startAngle={90} endAngle={-270}>
          <RadialBar dataKey="value" cornerRadius={20} background={{ fill: "#F1F5F9" }} isAnimationActive animationDuration={900} />
        </RadialBarChart>
      </ResponsiveContainer>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-lg font-bold font-mono text-slate-800">{pct}%</span>
        <span className="text-[10px] text-slate-400">{level}</span>
      </div>
    </div>
  );
}

function HigherOrderChart({ indexes, lang }) {
  const data = Object.entries(indexes || {}).map(([k, v]) => ({ name: trDim(k, lang) || humanize(k), value: v ?? 0 }));
  return (
    <ResponsiveContainer width="100%" height={Math.max(260, data.length * 34)}>
      <BarChart data={data} layout="vertical" margin={{ left: 8, right: 24 }}>
        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#F1F5F9" />
        <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11, fill: "#94A3B8" }} />
        <YAxis type="category" dataKey="name" width={220} tick={{ fontSize: 12, fill: "#475569" }} />
        <Tooltip cursor={{ fill: "#F8FAFC" }} />
        <ReferenceLine x={70} stroke="#94A3B8" strokeDasharray="4 4" label={{ value: "Strong (70+)", position: "insideTopRight", fontSize: 10, fill: "#94A3B8" }} />
        <Bar dataKey="value" radius={[0, 6, 6, 0]} isAnimationActive animationDuration={800}>
          {data.map((d, i) => (
            <Cell key={i} fill={d.value >= 70 ? "#10B981" : d.value >= 50 ? "#F59E0B" : "#F43F5E"} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

function CareerMatchRadar(props) {
  const { lang, ...c } = props;
  const data = CAREER_FIT_AXES.map((a) => ({ axis: tr(a.label, lang), value: c[a.key] ?? 0 }));
  return (
    <ResponsiveContainer width="100%" height={240}>
      <RadarChart data={data} outerRadius="60%" margin={{ top: 18, right: 44, bottom: 18, left: 44 }}>
        <PolarGrid stroke="#E2E8F0" />
        <PolarAngleAxis dataKey="axis" tick={{ fontSize: 9.5, fill: "#64748B" }} />
        <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
        <Radar dataKey="value" stroke="#4F46E5" fill="#4F46E5" fillOpacity={0.3} isAnimationActive animationDuration={700} />
      </RadarChart>
    </ResponsiveContainer>
  );
}

const SJT_EXPERT_NOTE = (
  "Note for counsellors & subject-matter experts: this section is included so you can review the situational "
  + "judgment reflected in each response yourself — not rely solely on the AI's interpretation below. It is "
  + "meant to support your own expert evaluation alongside the AI-generated read."
);

export default function ReportView({ report, isDemo, isSample, translateUrl }) {
  const [language, setLanguage] = useState("en");
  const [overlay, setOverlay] = useState(null);
  const [translating, setTranslating] = useState(false);
  if (!report) return null;
  const scores = language === "hi" && overlay ? { ...report.scores, ...overlay.scores } : report.scores;
  const narrative = language === "hi" && overlay ? overlay.narrative : report.narrative;
  const validity = scores?.response_validity;

  const handleLanguageChange = async (lang) => {
    if (lang === language) return;
    setLanguage(lang);
    if (lang === "hi" && !overlay && translateUrl) {
      setTranslating(true);
      try {
        const res = await api.get(translateUrl);
        setOverlay(res.data);
      } catch {
        toast.error("Could not translate report to Hindi. Please try again.");
        setLanguage("en");
      } finally {
        setTranslating(false);
      }
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-8" data-testid="report-view">
      <style>{`@media print { .print\\:hidden { display: none !important; } .print\\:break-inside-avoid { break-inside: avoid; } }`}</style>

      <style>{`@media print {
        .print\\:hidden { display: none !important; }
        .print\\:break-inside-avoid { break-inside: avoid; }
        .print-footer-fixed { position: fixed; bottom: 0; left: 0; right: 0; }
      }`}</style>

      <div className="flex items-center justify-between gap-3 flex-wrap" data-testid="report-branding-banner">
        <div className="hidden print:flex">
          <BrandLogo className="h-7" />
        </div>
        <span className="text-xs text-slate-400 font-mono" data-testid="report-version-date">
          Report v{report.report_version || "1.0"} · {new Date(report.generated_at).toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "numeric" })}
        </span>
        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-rose-600 bg-rose-50 border border-rose-200 rounded-full px-3 py-1 ml-auto" data-testid="report-confidential-badge">
          <Lock className="h-3 w-3" /> {tr("Confidential — Not for Sharing", language)}
        </span>
      </div>

      <div className="hidden print-footer-fixed print:flex items-center justify-center gap-2 py-2 text-[10px] text-slate-400 border-t border-slate-200 bg-white">
        <BrandLogo className="h-3" /> · careerstudy360.com · {tr("Confidential — Not for Sharing", language)} · v{report.report_version || "1.0"} · {new Date(report.generated_at).toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "numeric" })}
      </div>

      <div className="flex items-center justify-end gap-3 flex-wrap print:hidden">
        {translateUrl && (
          <div className="inline-flex items-center rounded-full border border-slate-200 bg-white p-1" data-testid="report-language-toggle">
            <button
              type="button"
              data-testid="report-language-en-button"
              onClick={() => handleLanguageChange("en")}
              className={cn("rounded-full px-3 py-1.5 text-xs font-medium transition-colors", language === "en" ? "bg-indigo-600 text-white" : "text-slate-500 hover:text-indigo-600")}
            >
              English
            </button>
            <button
              type="button"
              data-testid="report-language-hi-button"
              onClick={() => handleLanguageChange("hi")}
              disabled={translating}
              className={cn("rounded-full px-3 py-1.5 text-xs font-medium transition-colors flex items-center gap-1.5", language === "hi" ? "bg-indigo-600 text-white" : "text-slate-500 hover:text-indigo-600")}
            >
              {translating && <Loader2 className="h-3 w-3 animate-spin" />} हिंदी
            </button>
          </div>
        )}
        <button
          type="button"
          onClick={() => window.print()}
          data-testid="print-report-button"
          className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 hover:border-indigo-300 hover:text-indigo-600 transition-colors"
        >
          <Printer className="h-4 w-4" /> {tr("Print / Save as PDF", language)}
        </button>
      </div>

      {isDemo && (
        <div className="rounded-xl border-2 border-dashed border-amber-400 bg-amber-50 p-4 text-center" data-testid="demo-report-watermark">
          <p className="font-heading text-lg font-bold text-amber-700">{tr("DEMONSTRATION REPORT", language)}</p>
          <p className="text-xs text-amber-600 mt-1">{tr("Not a Valid CareerStudy360 Assessment", language)}</p>
        </div>

      )}

      {isSample && (
        <div className="rounded-xl border-2 border-dashed border-indigo-400 bg-indigo-50 p-4 text-center print:hidden" data-testid="sample-report-watermark">
          <p className="font-heading text-lg font-bold text-indigo-700">{tr("SAMPLE REPORT", language)}</p>
          <p className="text-xs text-indigo-600 mt-1">{tr("Illustrative example — not an individual student's actual results", language)}</p>
        </div>
      )}

      <Card testId="report-executive-summary" className="overflow-hidden relative">
        <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-indigo-500 via-fuchsia-500 to-amber-400 print:hidden" />
        <div className="flex items-start gap-6 flex-wrap sm:flex-nowrap">
          {validity && <ConfidenceGauge level={validity.level} />}
          <div className="flex-1 min-w-[200px]">
            <div className="flex items-start justify-between gap-4 flex-wrap mb-2">
              <p className="text-xs uppercase tracking-[0.2em] font-medium text-indigo-600 flex items-center gap-2">
                <Fingerprint className="h-4 w-4" /> {tr("Executive Career Profile", language)}
              </p>
              {validity && (
                <span
                  className={cn("text-xs font-medium rounded-full border px-3 py-1", CONFIDENCE_COLORS[validity.level] || "border-slate-300 bg-slate-50 text-slate-600")}
                  data-testid="assessment-confidence-badge"
                >
                  {tr("Assessment Confidence", language)}: {tr(validity.level, language)}
                </span>
              )}
            </div>
            <p className="text-lg text-slate-700 leading-relaxed" data-testid="report-snapshot">{narrative?.snapshot}</p>
            {narrative?.assessment_confidence_note && (
              <p className="text-sm text-slate-500 mt-4 leading-relaxed" data-testid="assessment-confidence-note">{narrative.assessment_confidence_note}</p>
            )}
          </div>
        </div>
      </Card>

      <Card testId="report-scholars-foundation" className="grid sm:grid-cols-[1fr_auto] gap-8 items-center">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-medium text-indigo-600 mb-2 flex items-center gap-2">
            <BookOpen className="h-4 w-4" /> {tr("Our Foundation", language)}
          </p>
          <h3 className="font-heading text-xl font-bold text-slate-900 mb-3">{tr("Grounded in Proven Models & Landmark Studies", language)}</h3>
          <p className="text-sm text-slate-600 leading-relaxed mb-4">
            {tr(
              "This report is not a single proprietary quiz — it is constructed and founded on decades of proven "
              + "behavioural-science models, informed by the latest studies published by career and personality scholars "
              + "worldwide, and cross-checked against established psychometric principles.",
              language
            )}
          </p>
          <ul className="grid sm:grid-cols-2 gap-2 mb-4">
            {SCHOLAR_CITATIONS.map((c, i) => (
              <li key={i} className="text-xs text-slate-500 flex items-start gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-indigo-400 mt-1.5 shrink-0" /> {tr(c, language)}
              </li>
            ))}
          </ul>
          <p className="text-xs text-slate-400 leading-relaxed border-t border-slate-100 pt-3">
            {tr(
              "CareerStudy360 is its own independent instrument — not a branded clone of any single existing test — "
              + "currently in a validation-ready stage, with its own large-scale reliability, validity, norming and fairness "
              + "studies underway.",
              language
            )}{" "}
            {tr("Read more on", language)}{" "}
            <a href="/science" className="text-indigo-500 underline">{tr("the science behind CareerStudy360", language)}</a>.
          </p>
        </div>
        <div className="w-56 mx-auto">
          <DimensionShareDonut scores={scores?.dimension_scores} dims={DIM_GROUPS[0].dims} lang={language} />
        </div>
      </Card>

      {scores?.response_pattern_remark && (
        <Card testId="report-response-pattern-remark" className="border-amber-200 bg-amber-50/60">
          <h3 className="font-heading text-base font-semibold text-slate-900 mb-2 flex items-center gap-2">
            <Timer className="h-4 w-4 text-amber-600" /> {tr("Response Pace Observation", language)}
          </h3>
          <p className="text-sm text-slate-600 leading-relaxed" data-testid="response-pattern-remark">{scores.response_pattern_remark}</p>
        </Card>
      )}

      {DIM_GROUPS.map((g) => (
        <Card key={g.key} testId={`report-group-${g.key}`} className={g.chart === "radar" ? "bg-gradient-to-br from-indigo-50/60 via-white to-fuchsia-50/40" : ""}>
          <h3 className="font-heading text-lg font-semibold text-slate-900 mb-5 flex items-center gap-2">
            <g.icon className="h-5 w-5" style={{ color: GROUP_COLORS[g.key] }} /> {tr(g.label, language)}
          </h3>
          {g.chart === "radar" ? <RiasecShowcase dims={g.dims} scores={scores?.dimension_scores} lang={language} /> : <GroupBarChart group={g} scores={scores?.dimension_scores} lang={language} />}
          {g.archetypeInfo && (
            <div className="mt-6">
              <ArchetypeGrid dims={g.dims} scores={scores?.dimension_scores} infoMap={g.archetypeInfo} testId={`${g.key}-archetype-grid`} lang={language} />
            </div>
          )}
          {narrative?.[g.narrativeKey] && (
            <p className="text-sm text-slate-600 mt-6 leading-relaxed">{narrative[g.narrativeKey]}</p>
          )}
          {g.key === "cognitive_aptitude" && scores?.aptitude_blended && narrative?.aptitude_test_insights && (
            <div className="mt-4 rounded-lg border border-violet-200 bg-violet-50 px-4 py-3" data-testid="aptitude-blend-note">
              <p className="text-xs font-semibold text-violet-700 mb-1 flex items-center gap-1.5">
                <Sparkles className="h-3.5 w-3.5" /> {tr("Reinforced With Aptitude Test Performance Data", language)}
              </p>
              <p className="text-xs text-violet-600 leading-relaxed">{narrative.aptitude_test_insights}</p>
            </div>
          )}
          {g.key === "professional_strengths" && scores?.aptitude_blended && narrative?.ideation_and_work_style_note && (
            <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3" data-testid="aptitude-ideation-note">
              <p className="text-xs font-semibold text-emerald-700 mb-1 flex items-center gap-1.5">
                <Lightbulb className="h-3.5 w-3.5" /> {tr("From Your Aptitude Test — Idea Generation & Work Style", language)}
              </p>
              <p className="text-xs text-emerald-600 leading-relaxed">{narrative.ideation_and_work_style_note}</p>
            </div>
          )}
        </Card>
      ))}

      <Card testId="report-group-conflict-resilience">
        <h3 className="font-heading text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-indigo-600" /> {tr("Conflict, Decision Style & Resilience", language)}
        </h3>
        <p className="text-sm text-slate-600 leading-relaxed">{narrative?.conflict_decision_style || narrative?.conflict_management_negotiation}</p>
        <p className="text-sm text-slate-600 mt-4 leading-relaxed">{narrative?.resilience_and_derailers}</p>
      </Card>

      {narrative?.work_environment_preferences && (
        <Card testId="report-group-work-environment">
          <h3 className="font-heading text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
            <Lightbulb className="h-5 w-5 text-indigo-600" /> {tr("Work Environment Preferences", language)}
          </h3>
          <p className="text-sm text-slate-600 leading-relaxed">{narrative.work_environment_preferences}</p>
        </Card>
      )}

      <Card testId="report-higher-order-indexes">
        <h3 className="font-heading text-lg font-semibold text-slate-900 mb-5 flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-indigo-600" /> {tr("Higher-Order Professional Potential Indexes", language)}
        </h3>
        <HigherOrderChart indexes={scores?.higher_order_indexes} lang={language} />
      </Card>

      <Card testId="report-strengths">
        <h3 className="font-heading text-lg font-semibold text-slate-900 mb-5 flex items-center gap-2">
          <Award className="h-5 w-5 text-indigo-600" /> {tr("Strengths", language)}
        </h3>
        <div className="flex flex-wrap gap-2">
          {(narrative?.strengths || []).map((s, i) => (
            <Badge key={i} variant="secondary" className="rounded-full px-3 py-1">{s}</Badge>
          ))}
        </div>
      </Card>

      <div>
        <h3 className="font-heading text-xl font-semibold text-slate-900 mb-5 flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-indigo-600" /> {tr("Top Career Matches", language)}
        </h3>
        <div className="space-y-4">
          {(scores?.career_matches || []).map((c) => {
            const cn_ = narrative?.career_matches_narrative?.[c.occupation_id];
            return (
              <Card key={c.occupation_id} testId={`career-match-card-${c.occupation_id}`}>
                <div className="grid sm:grid-cols-[1fr_auto] gap-6">
                  <div>
                    <div>
                      <p className="text-xs text-indigo-600 font-medium">{c.family}</p>
                      <h4 className="font-heading text-lg font-semibold text-slate-900">{c.name}</h4>
                      <p className="text-sm text-slate-500 mt-1">{c.description}</p>
                    </div>
                    {cn_ && (
                      <div className="mt-5 grid sm:grid-cols-3 gap-4 text-sm border-t border-slate-100 pt-4">
                        <div><p className="font-medium text-slate-700 mb-1">{tr("Why This Career Appears", language)}</p><p className="text-slate-500">{cn_.why_this_career_appears}</p></div>
                        <div><p className="font-medium text-slate-700 mb-1">{tr("What Supports the Match", language)}</p><p className="text-slate-500">{cn_.what_supports_the_match}</p></div>
                        <div><p className="font-medium text-slate-700 mb-1">{tr("What May Need Development", language)}</p><p className="text-slate-500">{cn_.what_may_need_development}</p></div>
                      </div>
                    )}
                  </div>
                  <div className="w-full sm:w-60 flex flex-col">
                    <CareerMatchRadar {...c} lang={language} />
                    <div className="text-right shrink-0 -mt-2" data-testid={`career-match-alignment-${c.occupation_id}`}>
                      <p className="text-3xl font-bold text-indigo-600 font-mono">{c.overall_alignment ?? "—"}%</p>
                      <p className="text-xs text-slate-400">{tr("Overall Alignment", language)}</p>
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </div>

      <ListSection icon={Sparkles} title={tr("Unexpected / Hidden Career Possibilities", language)} items={narrative?.hidden_career_possibilities} testId="report-hidden-careers" />
      <ListSection icon={Award} title={tr("Development Priorities", language)} items={narrative?.development_priorities} testId="report-development-priorities" />
      <ListSection icon={ShieldCheck} title={tr("Potential Mismatches & Development Areas", language)} items={narrative?.mismatches_and_development_areas} testId="report-mismatches" />
      <ListSection icon={GraduationCap} title={tr("Education / Training Directions", language)} items={narrative?.education_training_directions} testId="report-education-directions" />

      {scores?.broad_domain_fit && (
        <Card testId="report-broad-domain-fit">
          <h3 className="font-heading text-lg font-semibold text-slate-900 mb-1 flex items-center gap-2">
            <Compass className="h-5 w-5 text-indigo-600" /> {tr("Fit Into Common Career Tracks", language)}
          </h3>
          <p className="text-sm text-slate-500 mb-5">
            {tr("A quick directional snapshot of how this profile aligns with four widely-considered study/career tracks.", language)}
          </p>
          <div className="grid sm:grid-cols-2 gap-4">
            {Object.entries(DOMAIN_FIT_META).map(([key, meta]) => {
              const fit = scores.broad_domain_fit[key];
              if (!fit || fit.tier == null) return null;
              const Icon = meta.icon;
              return (
                <div key={key} data-testid={`domain-fit-card-${key}`} className="rounded-xl border border-slate-200 p-4 flex items-start gap-3">
                  <span className="h-9 w-9 rounded-lg bg-indigo-50 flex items-center justify-center shrink-0">
                    <Icon className="h-4.5 w-4.5 text-indigo-600" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 flex-wrap mb-1">
                      <p className="text-sm font-semibold text-slate-900">{tr(meta.label, language)}</p>
                      <span className={cn("text-[11px] font-semibold rounded-full border px-2 py-0.5 shrink-0", FIT_TIER_COLORS[fit.tier])} data-testid={`domain-fit-tier-${key}`}>
                        {tr(fit.tier, language)}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500">{tr(meta.blurb, language)}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      <motion.div {...fadeUp} className="rounded-2xl bg-gradient-to-br from-indigo-600 to-cyan-600 p-8 text-white">
        <h3 className="font-heading text-lg font-semibold mb-3">{tr("Closing Summary", language)}</h3>
        <p className="text-indigo-50 leading-relaxed">{narrative?.closing_summary}</p>
      </motion.div>

      <Card testId="report-disclaimer" className="border-slate-300 bg-slate-50">
        <h3 className="font-heading text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-amber-500" /> {tr("Disclaimer", language)}
        </h3>
        <p className="text-sm text-slate-600 leading-relaxed">
          This report is generated using AI-assisted psychometric analysis based on the responses provided during
          the CareerStudy360 assessment, using the latest AI tools and technology available at the time of
          assessment. It is intended purely as a directional guide to support self-awareness and career
          exploration, and does <strong>not</strong> constitute a definitive, clinical, or professional assessment
          of an individual's actual capability, intelligence, or potential. Real-world academic performance,
          experience, effort, and personal circumstances may differ significantly from what is reflected here.
          This report should be used as one input among several — alongside guidance from teachers, parents and
          career counsellors — and not as the sole basis for any academic or career decision.
        </p>
        <p className="text-xs text-slate-400 mt-4 flex items-center gap-1.5">
          <Lock className="h-3 w-3" /> CareerStudy360 · careerstudy360.com · Confidential — Not for Sharing
        </p>
      </Card>

      {scores?.sjt_annex?.length > 0 && (
        <Card testId="report-sjt-annex" className="print:break-before-page">
          <h3 className="font-heading text-lg font-semibold text-slate-900 mb-1 flex items-center gap-2">
            <ScrollText className="h-5 w-5 text-indigo-600" /> {tr("Annex A — Situational Judgment Response Review", language)}
          </h3>
          <p className="text-sm text-slate-500 mb-3">
            {tr("A review of how you responded to the real-world scenarios in this assessment, with a short AI-generated interpretation of each choice. This annex does not reveal a scoring key for any scenario.", language)}
          </p>
          <div className="flex items-start gap-2 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3.5 py-3 mb-5" data-testid="sjt-annex-expert-note">
            <Info className="h-3.5 w-3.5 text-indigo-500 mt-0.5 shrink-0" />
            <p className="leading-relaxed">{tr(SJT_EXPERT_NOTE, language)}</p>
          </div>
          <div className="space-y-4">
            {scores.sjt_annex.map((item, i) => (
              <div key={item.slot_id || i} data-testid={`sjt-annex-item-${i}`} className="rounded-xl border border-slate-200 p-4 print:break-inside-avoid">
                <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
                  <p className="text-xs uppercase tracking-wide font-medium text-slate-400">{tr("Scenario", language)} {i + 1} · {trDim(item.dimension, language) || humanize(item.dimension)}</p>
                  {item.tier && (
                    <span className={cn("text-[11px] font-semibold rounded-full border px-2 py-0.5 shrink-0", SJT_TIER_COLORS[item.tier])} data-testid={`sjt-annex-tier-${i}`}>
                      {tr(item.tier, language)}
                    </span>
                  )}
                </div>
                <p className="text-sm text-slate-700 leading-relaxed mb-2">{item.scenario}</p>
                <p className="text-sm text-slate-500 mb-2"><span className="font-medium text-slate-600">{tr("Your response:", language)}</span> {item.your_response}</p>
                {item.ai_interpretation && (
                  <p className="text-xs text-indigo-600 bg-indigo-50 rounded-lg px-3 py-2" data-testid={`sjt-annex-interpretation-${i}`}>{item.ai_interpretation}</p>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
